import CenterFocusWeakOutlinedIcon from "@mui/icons-material/CenterFocusWeakOutlined";
import ZoomInOutlinedIcon from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlinedIcon from "@mui/icons-material/ZoomOutOutlined";
import { TooltipButton } from "@/components/common/TooltipButton";
import React, { useEffect, useRef } from "react";
import { mountGraph, type GraphHandle } from "@embed-graph";
import { useThemeStore } from "@/components/main/stores/themeStore";
import type { DraftTable } from "@/lib/api/apps/types";
import { linkTarget } from "./draft-helpers";

/** What the embed widget draws (client/embed_graph/web/embed-model.schema.json):
 *  `public` tables are drawn dashed, as outside the set. */
export interface EmbedModel {
  tables: {
    id: string;
    name: string;
    kind: "mine" | "public";
    columns: { name: string; type?: string }[];
  }[];
  links: {
    from: { table: string; column: string };
    to: { table: string; column: string };
  }[];
}

/** A version as the embed widget's model. */
export function draftEmbedModel(tables: DraftTable[]): EmbedModel {
  return {
    tables: tables.map((t) => ({
      id: t.id,
      name: t.name,
      kind: "mine" as const,
      columns: t.columns.map((c) => ({ name: c.name, type: c.data_type })),
    })),
    links: tables.flatMap((t) =>
      t.columns.flatMap((c) => {
        const target = tables.find((x) => x.id === c.link_to_table);
        const column = target && linkTarget(target);
        return target && column
          ? [
              {
                from: { table: t.id, column: c.name },
                to: { table: target.id, column },
              },
            ]
          : [];
      }),
    ),
  };
}

/** Tables drawn by the graph engine the canvas uses: a draft version as the
 *  target model will show it, or the tables lent to a research app. */
export const DraftGraph: React.FC<{
  model: EmbedModel;
  highlighted: string | null;
  /** The table picked in the list or here. */
  selected: string | null;
  onPick: (tableId: string | null) => void;
  /** Laid over the graph, inside its box. */
  children?: React.ReactNode;
}> = ({ model: shape, highlighted, selected, onPick, children }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const handle = useRef<GraphHandle | null>(null);
  const theme = useThemeStore((s) => s.theme);
  const json = JSON.stringify(shape);
  const model = json;
  const latest = useRef({ model, theme, onPick, selected });
  latest.current = { model, theme, onPick, selected };
  // A pick made here is already selected and in view: moving to it again would jump.
  const pickedHere = useRef<string | null>(null);

  useEffect(() => {
    let gone = false;
    const canvas = canvasRef.current;
    const box = boxRef.current;
    if (!canvas || !box) return;
    void mountGraph({
      canvas,
      sizeTarget: box,
      model: latest.current.model,
      theme: latest.current.theme,
      onSelect: (id) => {
        pickedHere.current = id;
        latest.current.onPick(id);
      },
    }).then((h) => {
      if (gone) return h.destroy();
      handle.current = h;
      pickedHere.current = latest.current.selected;
      h.app.set_selected(latest.current.selected ?? "");
      h.kick();
    });
    return () => {
      gone = true;
      handle.current?.destroy();
      handle.current = null;
    };
  }, []);

  useEffect(() => {
    const h = handle.current;
    if (!h) return;
    h.app.load(model);
    h.app.set_selected(pickedHere.current ?? "");
    h.kick();
  }, [model]);

  useEffect(() => {
    handle.current?.app.set_theme(theme);
    handle.current?.kick();
  }, [theme]);

  useEffect(() => {
    handle.current?.app.set_soft_hover(highlighted ?? "");
    handle.current?.kick();
  }, [highlighted]);

  useEffect(() => {
    const h = handle.current;
    if (!h || selected === pickedHere.current) return;
    pickedHere.current = selected;
    h.app.set_selected(selected ?? "");
    if (selected) h.app.pan_to_nodes(JSON.stringify([selected]));
    h.kick();
  }, [selected]);

  // A step is two full wheel notches about the middle, about a third.
  const zoom = (direction: 1 | -1) => {
    const h = handle.current;
    const box = boxRef.current;
    if (!h || !box) return;
    for (let i = 0; i < 2; i++)
      h.app.wheel(direction * 90, box.clientWidth / 2, box.clientHeight / 2);
    h.kick();
  };

  return (
    <div
      ref={boxRef}
      className="relative w-full flex-1 min-h-80 lg:min-h-0 rounded-app border border-border bg-bg-subtle overflow-hidden"
    >
      <canvas ref={canvasRef} className="absolute inset-0" />
      {children}
      {/* The canvas toolbar's zoom and fit, on the graph's own camera. */}
      <div className="absolute right-2 bottom-2 flex gap-0.5 p-0.5 rounded-app border border-border bg-bg">
        <TooltipButton
          title="Zoom Out"
          variant="default"
          size="sm"
          onClick={() => zoom(1)}
        >
          <ZoomOutOutlinedIcon />
        </TooltipButton>
        <TooltipButton
          title="Zoom In"
          variant="default"
          size="sm"
          onClick={() => zoom(-1)}
        >
          <ZoomInOutlinedIcon />
        </TooltipButton>
        <TooltipButton
          title="Fit to Content"
          variant="default"
          size="sm"
          onClick={() => {
            handle.current?.app.focus_reveal();
            handle.current?.kick();
          }}
        >
          <CenterFocusWeakOutlinedIcon />
        </TooltipButton>
      </div>
    </div>
  );
};
